/**
 * Divider — shared types, Zod prop schema, and token constants.
 *
 * A decorative rule separating stacked content (or, in `vertical`
 * orientation, grouping a nested block under its parent — e.g. a connector
 * line). Hidden from assistive tech on both platforms; carries no semantics
 * and no events.
 *
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 */

import { z } from 'zod';
import { colors } from '../../elements/colors';
import { states } from '../../elements/states';

export const DividerOrientation = z.enum(['horizontal', 'vertical']);
export type DividerOrientation = z.infer<typeof DividerOrientation>;

/** Rule treatment — `solid` (the 1dp rule) or the Figma dashed rule. */
export const DividerLine = z.enum(['solid', 'dashed']);
export type DividerLine = z.infer<typeof DividerLine>;

export const dividerPropsSchema = z.object({
  /** `horizontal` (default) spans the full width; `vertical` spans the full height — e.g. a connector line grouping a nested option list under its parent. */
  orientation: DividerOrientation.default('horizontal'),
  /** `dashed` renders the horizontal rule as the Figma dashed treatment
   * (`DIVIDER_DASHED_WEIGHT`-thick, `stroke/divider` by default) — grouped-card
   * row separators. Orthogonal to `orientation`; the vertical connector stays
   * solid. */
  line: DividerLine.default('solid'),
  /** Override the default `DIVIDER_COLOR` — e.g. a connector line matching a
   * specific token (`colors.stroke.divider`) rather than the default rule
   * color, which is tuned for the divider's original (non-white) surface. */
  color: z.string().optional(),
  testID: z.string().optional(),
});

// Input shape so the defaulted `orientation` field stays optional for callers.
export type DividerProps = z.input<typeof dividerPropsSchema>;

export const DIVIDER_COLOR = colors.stroke.container;

// Figma-pinned "2-3pt" connector-line thickness for the vertical orientation —
// no exact stroke-width token covers this specific use, so it's a literal
// (matching the established repo pattern for other Figma-pinned dimensions).
// Half its own width gives the line rounded caps.
export const DIVIDER_VERTICAL_WIDTH = 2;
export const DIVIDER_VERTICAL_RADIUS = DIVIDER_VERTICAL_WIDTH / 2;

// Figma-pinned dashed-rule treatment (grouped-card row separators): a 2px
// weight in the divider stroke token, with the same dash rhythm the profile
// goals card established. Native draws it as an SVG line — RN's dashed
// `borderStyle` on a partial border is unreliable on iOS.
export const DIVIDER_DASHED_WEIGHT = 2;

/**
 * Solid rule weight, in dp. The designs specify a 1dp rule and the web
 * implementation has always rendered `height: 1`; native used
 * `StyleSheet.hairlineWidth` (0.33dp at 3x), so the same `Divider` drew visibly
 * fainter on a phone than in the design or the browser. Both platforms now read
 * this constant, so the rule is one value in one place.
 */
export const DIVIDER_SOLID_WEIGHT = states.borderWidth.default;
export const DIVIDER_DASHED_COLOR = colors.stroke.divider;
export const DIVIDER_DASH_PATTERN = '4 4';
